import { useEffect, useState } from 'react';

import { useGlobalDialog } from "../../Store/DialogManager";

import { useSnackbar } from "notistack";
import styled from 'styled-components';
import { QRCodeSVG } from 'qrcode.react';
import { Typography } from '@mui/material';

import { useAuthenticationHook } from '../../Hooks/Authentication/authenticationHooks';

import { QRCodeModalInterface, TwoWayFactorData } from '../../Models/AdminUserControl/AdminUserControl';

import RequiredParametersHelper from '../../Utilities/Helpers/RequiredParametersHelper';
import RequiredFieldHelper from '../../Utilities/Helpers/RequiredFieldHelper';
import QueryBuilderHelper from '../../Utilities/Helpers/QueryBuilderHelper';

import { ButtonType } from '../../Utilities/Enums/ButtonTypeEnum';
import { AdminUserField, TwoWayFactorDataField } from '../../Utilities/Enums/ParametersEnum';
import { CachingEnum } from '../../Utilities/Enums/CachingEnum';

import CustomButton from '../../CustomViews/Elements/CustomButton';
import FormTextField from '../../CustomViews/Elements/FormTextField';
import AutoCompleteDropdown from '../../CustomViews/Elements/AutoCompleteDropdown';
import CompanyDropdown from '../../CustomViews/Elements/AutoComplete/CompanyDropdown';

const ModalContentContainer = styled.div <{ $darkMode?: boolean; }>`
    display: grid;
    grid-template-columns: 1fr;
    gap: 10px;
    width: 30vw;

    .twofaSetupContainer {
        .container2fa {
            display: grid;
            grid-template-columns: 1fr 1fr;
            align-items: center;
            gap: 30px;

            .stepContainer {
                grid-column: span 2;
                // margin: 30px 0px 0px 0px;

                .stepText,
                .stepDescription {
                    font-size: 12px;
                }
        
                .stepheaderText {
                    font-size: 14px;
                    font-weight: 600;
                }
            }

            .twoRow {
                grid-column: unset !important;
            }

            .formTextFieldContainer,
            button {
                grid-column: span 2;
                width: 100%;
            }            
        }
    }
`;

export default function QRCodeModal(props: QRCodeModalInterface) {
    let { openModal, setopenModal, formData, getAdminUserData } = props;
    const { enqueueSnackbar } = useSnackbar();
    const { setDialogContent, setOpenGlobalDialog } = useGlobalDialog();
    let authenticationRequiredFields = RequiredParametersHelper.authenticationCode();

    //Use States
    let [errorMessages, setErrorMessages]: any = useState({});
    let [TwoWayFactor, setTwoWayFactor] = useState<TwoWayFactorData>(new TwoWayFactorData());
    let [authenticationCode, setauthenticationCode] = useState<any>("");

    const { setup2FA, confirm2FA, disable2FA } = useAuthenticationHook();

    //Variables
    const authenticationCodeProps = {
        name: "code",
        title: 'Authentication Code',
        fieldValue: authenticationCode ?? "",
        type: 'text',
        required: authenticationRequiredFields?.find((field: any) => field.keyName == "code")?.keyName != null,
        errorMessages: errorMessages,
        maxLength: 6,
        handleOnChange: (selectedValue: any) => {
            setauthenticationCode(selectedValue);
        },
    };


    //Event Handlers
    const generateQRCode = async () => {
            let _setup2FA: any = null;

            if(formData?.[AdminUserField.PUBLIC_ID] != null && formData?.[AdminUserField.PUBLIC_ID] != "") {
                _setup2FA = await setup2FA(JSON.stringify({
                    userId: formData?.[AdminUserField.PUBLIC_ID]
                }));
            }

            setTwoWayFactor((prev: any) => ({
                ...prev, 
                [TwoWayFactorDataField.OTP_AUTH_URL]: _setup2FA?.data?.data?.[TwoWayFactorDataField.OTP_AUTH_URL] ?? "",
                [TwoWayFactorDataField.SECRET_KEY]: _setup2FA?.data?.data?.[TwoWayFactorDataField.SECRET_KEY] ?? "",
            }));
    }

    const onCloseDialog = async (e: any = null) => {
        if(e) {
            e.preventDefault();
        }

        if(setopenModal) {
            setopenModal(false);
        }

        setOpenGlobalDialog(false);
    }

    useEffect(() => {
        if(openModal) {
            setDialogContent({
                title: (formData?.[AdminUserField.ENABLE_2FA] ? "Disabling" : "Enabling") + " Two-factor Authentication",
                clearDialog: onCloseDialog,
                onCloseDialog: onCloseDialog,
                actionButtons: renderActionButtons(),
                children: renderDialogContent(),
            });
        }
    }, [openModal, errorMessages, TwoWayFactor, authenticationCode]);

    useEffect(() => {
        if(!formData?.[AdminUserField.ENABLE_2FA]) {
            generateQRCode();
        }
    }, []);

    const handleSubmitAuthenticationCode = async () => {
        let _formData = {
            userId: formData?.[AdminUserField.PUBLIC_ID] ?? "",
            code: authenticationCode ?? "",
        }

        let _validateForm = RequiredFieldHelper.validateForm(authenticationRequiredFields, _formData, errorMessages, setErrorMessages, enqueueSnackbar);

        if (_validateForm?.hasError) { return true; }

        let twoFAResponse: any = null;

        if(formData?.[AdminUserField.ENABLE_2FA]) {
            twoFAResponse = await disable2FA(JSON.stringify(_formData));
        } else {
            twoFAResponse = await confirm2FA(JSON.stringify(_formData));
        }

        let _success = twoFAResponse?.isSuccess ?? false;

        if(_success && getAdminUserData) {
            getAdminUserData();
            onCloseDialog();
        }

    }

    //Rendering Functions
    const renderActionButtons = () => {
        return <>
            <CustomButton
                btnType={ButtonType.CANCEL}
                title='Close'
                onClick={(e: any) => {
                    onCloseDialog(e);
                }} />

            <CustomButton
                btnType={ButtonType.PRIMARY}
                title={'Verify and ' + (formData?.[AdminUserField.ENABLE_2FA] ? "Deactivate" : "Activate")}
                onClick={(e: any) => {
                    handleSubmitAuthenticationCode();
                }} />
        </>
    }

    const renderStep = (stepNumber: number, stepHeader: any, stepDescription: any, is2Row: boolean = false) => {
        return <div className={"stepContainer" + (is2Row ? " twoRow" : "")}>
            {stepNumber > 0 && <Typography className="stepText" color="primaryText">Step {stepNumber}</Typography>}
            <Typography className="stepheaderText" color="primaryText">{stepHeader ?? ""}</Typography>
            <Typography className="stepDescription" color="primaryText">{stepDescription ?? ""}</Typography>
        </div>
    }

    const renderTwoFASetup = () => {
        return <div className="twofaSetupContainer">
            <div className="container2fa">
                {!formData?.[AdminUserField.ENABLE_2FA] && renderStep(1, "Download App", "Download a mobile authentication app like Google Authenticator")}
                {!formData?.[AdminUserField.ENABLE_2FA] && renderStep(2, "Scan this QR Code", "Scan QR code using a mobile authentication app to generate a verification code", true)}

                {!formData?.[AdminUserField.ENABLE_2FA] && <QRCodeSVG
                    value={TwoWayFactor?.[TwoWayFactorDataField.OTP_AUTH_URL] ?? ""}
                    size={256}
                    bgColor="#ffffff"
                    fgColor="#000000"
                    // level="L"
                    // includeMargin={true} 
                />}

                {!formData?.[AdminUserField.ENABLE_2FA] && renderStep(3, "Enter Verification Code", "")}

                {formData?.[AdminUserField.ENABLE_2FA] && renderStep(0, "", "Kindly put authentication code to disable the Two-factor Authentication")}

                <FormTextField {...authenticationCodeProps} />

                {/*<CustomButton
                        btnType={ButtonType.PRIMARY}
                        title={"Verify and Activate"}
                        onClick={(e: any) => {
                            if(handleSubmitAuthenticationCode) {
                                handleSubmitAuthenticationCode(authenticationCode);
                            }
                        }} />*/}
            </div>
        </div>
    }

    const renderDialogContent = () => {
        return <ModalContentContainer>
            {renderTwoFASetup()}
        </ModalContentContainer>
    }

    return <></>
}